import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import { useApiConfig } from "src/api/ApiConfigProvider";
import type { AutocompletePlace } from "src/api/v1.6/autocomplete";
import { Button as ReactAriaButton } from "react-aria-components";
import type { GeocodedPlace } from "src/GeocodedPlace";
import CheckCircleSolid from "src/design-system/icons/CheckCircleSolid.svg?react";
import X from "src/design-system/icons/X.svg?react";
import ChevronLeft from "src/design-system/icons/ChevronLeft.svg?react";
import Rome2Rio from "src/design-system/icons/Rome2Rio.svg?react";
import { iconSize } from "src/theme";
import { autocompleteToGeocodedPlace } from "src/utils/autocompleteToGeocodedPlace";
import { localeToLanguageCode } from "src/utils/conversions/languageCode";
import { useAnalyticsPageView } from "src/utils/hooks/useAnalyticsPageView";
import { useScreenMinWidth } from "src/utils/hooks/useScreenMinWidth";
import useSearch from "src/utils/hooks/useSearch";
import { useTypedLocation } from "src/utils/hooks/useTypedLocation";
import useUser from "src/utils/hooks/useUser";
import { navigateToNewStateHash } from "src/utils/location/navigateToNewStateHash";
import { tw } from "src/design-system/tw";
import { Button } from "src/design-system/components/Button/Button";
import { useTripPlannerContext } from "../hooks/useTripPlannerContext";
import { PlaceInputList } from "../TripEditorModal/PlaceInputList";
import messages from "./CreateTripScreen.messages";

export function CreateTripScreen() {
  const intl = useIntl();
  const { dispatch } = useTripPlannerContext();
  const navigate = useNavigate();
  const location = useTypedLocation();
  const apiConfig = useApiConfig();
  const languageCode = localeToLanguageCode(intl.locale);
  const queryClient = useQueryClient();
  const { user } = useUser();
  const isUserLoggedIn = !!user?.id;
  const [tripDestinations, setTripDestinations] = useState<GeocodedPlace[]>([]);
  const { isSmScreen, isMobile, isDesktop } = useScreenMinWidth();
  const { searchResponse } = useSearch();
  const [loadingIndex, setLoadingIndex] = useState<number | undefined>();

  const getPlaces = useCallback(() => {
    return Array.from(
      { length: Math.max(tripDestinations.length, 2) },
      (_, index) => {
        const geocodedPlace = tripDestinations[index];
        if (geocodedPlace) {
          return geocodedPlace.longName ?? geocodedPlace.shortName;
        }
        return "";
      },
    );
  }, [tripDestinations]);

  useAnalyticsPageView(
    {
      pagePath: location.pathname + location.search + location.hash,
      pageLocation: window.location.href,
    },
    searchResponse,
    "CreateTrip",
    undefined,
  );

  function handleSelectDestination(
    autocompletePlace: AutocompletePlace,
    index: number,
  ) {
    setLoadingIndex(index);
    sendAnalyticsInteractionEvent({
      category: "CreateTrip",
      action: "Select:EditDestination",
      label: autocompletePlace.canonicalName,
    });
    autocompleteToGeocodedPlace(
      autocompletePlace,
      (geocodedPlace: GeocodedPlace) => {
        setTripDestinations((prev) => {
          const newDestinations = [...prev];
          newDestinations[index] = geocodedPlace;
          return newDestinations;
        });
        setLoadingIndex(undefined);
      },
      languageCode,
      queryClient,
      apiConfig,
    );
  }

  function handleCloseCreateTrip() {
    sendAnalyticsInteractionEvent({
      category: "CreateTrip",
      action: "Click:Close",
    });
    navigateToNewStateHash(
      navigate,
      {
        highlightedTab: "trips",
      },
      `#trips/saved/`,
      {
        ...location,
      },
    );
  }

  function handleRemoveDestination(index: number) {
    sendAnalyticsInteractionEvent({
      category: "CreateTrip",
      action: "Click:ClearDestination",
    });
    const newTripDestinations = tripDestinations.filter((_, i) => i !== index);
    setTripDestinations(newTripDestinations);
  }

  function handleCreateTrip() {
    sendAnalyticsInteractionEvent({
      category: "CreateTrip",
      action: "Click:CreateTrip",
    });
    // If the user is logged in under multiple trips, we want to create a new trip.
    // If the user is not logged in, we want to replace the trip.
    const action = user?.id
      ? "CREATE_TRIP_FROM_SEARCH_TRIP"
      : "REPLACE_TRIP_FROM_SEARCH_TRIP";
    dispatch({
      type: action,
      places: tripDestinations,
    });
    navigateToNewStateHash(
      navigate,
      {
        highlightedTab: "trips",
      },
      `#trips`,
      {
        ...location,
      },
    );
  }

  return (
    <div
      className={tw(
        "relative min-h-svh w-full px-4 pt-6 pb-4",
        "md:bg-fill-default md:pr-1",
      )}
    >
      {isUserLoggedIn && isDesktop && (
        <ReactAriaButton
          className="mb-6 flex items-center gap-1 hover:cursor-pointer"
          onClick={handleCloseCreateTrip}
        >
          <ChevronLeft width={iconSize.lg} height={iconSize.lg} />
          <span className="text-body-lg hover:underline">
            {intl.formatMessage(messages.back)}
          </span>
        </ReactAriaButton>
      )}
      <div className="mb-6 flex w-full items-center justify-between md:hidden">
        <a href="/">
          <Rome2Rio
            title={intl.formatMessage(messages.homepage)}
            className="w-[120px]"
          />
        </a>
        {isUserLoggedIn && isMobile && (
          <ReactAriaButton
            onClick={handleCloseCreateTrip}
            className="mx-2 my-0"
          >
            <X className="h-5.5 w-5.5 text-icon-primary" />
          </ReactAriaButton>
        )}
      </div>
      <div className="mb-4">
        <span className="text-heading-md">
          {isUserLoggedIn
            ? intl.formatMessage(messages.loggedInHeading)
            : intl.formatMessage(messages.heading)}
        </span>
      </div>
      <div className="md:rounded-lg md:bg-fill-solid md:p-6">
        <div className="pb-4 md:pb-0">
          <PlaceInputList
            loadingIndex={loadingIndex}
            places={getPlaces()}
            handleRemoveDestination={handleRemoveDestination}
            handleSelectPlace={handleSelectDestination}
            isInline={isSmScreen}
          />
        </div>
        <div className="md:my-1">
          <Button
            size="large"
            onPress={handleCreateTrip}
            isDisabled={tripDestinations.length < 2}
            className="w-full"
          >
            {intl.formatMessage(messages.cta)}
          </Button>
        </div>
      </div>
      {isDesktop && (
        <div className="mt-6 ml-4">
          <span className="text-heading-sm">
            {intl.formatMessage(messages.withTrips)}:
          </span>
          <ul className="mt-4 list-none p-0">
            <li className="mb-1.5 flex items-center">
              <CheckCircleSolid className="h-6 w-6 text-icon-success" />
              <span className="ml-1.5 text-body-md">
                {intl.formatMessage(messages.addDestinations)}
              </span>
            </li>
            <li className="mb-1.5 flex items-center">
              <CheckCircleSolid className="h-6 w-6 text-icon-success" />
              <span className="ml-1.5 text-body-md">
                {intl.formatMessage(messages.saveTrips)}
              </span>
            </li>
            <li className="mb-1.5 flex items-center">
              <CheckCircleSolid className="h-6 w-6 text-icon-success" />
              <span className="ml-1.5 text-body-md">
                {intl.formatMessage(messages.bestWays)}
              </span>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
